import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import CategoryFeedback, { CategoryFeedbackValue } from './CategoryFeedback';
import { QcOutcome } from './imageMeta';
import { QcCategoriesState } from './qcCategories';

/** Everything a reviewer can say about one photograph. */
export type ImageAssessmentValue = CategoryFeedbackValue & {
    outcome: QcOutcome | null;
};

interface ImageAssessmentProps {
    /**
     * The categories for THIS image's eCRF field — the universal section plus the one for
     * its shot type — and where that fetch has got to.
     */
    categories: QcCategoriesState;
    value: ImageAssessmentValue;
    onChange: (next: ImageAssessmentValue) => void;
    /** The visit is approved (LOCKED): render the verdict and feedback as static text. */
    readOnly?: boolean;
}

/**
 * SKIN-241 — the verdict on one photograph, and the feedback attached to it.
 *
 * Controlled: it holds no state and emits a whole `ImageAssessmentValue` on every change,
 * because the value is upserted whole — the API replaces categories rather than merging
 * them, and an absent comment clears the stored note. A component keeping half the value
 * locally would let the two drift.
 */
export default function ImageAssessment({
    categories,
    value,
    onChange,
    readOnly = false,
}: ImageAssessmentProps) {
    /**
     * Feedback exists under `needs` and nowhere else — stated once, for every transition.
     *
     * Writing it as a special case on the `correct` branch left a hole review found: MUI's
     * exclusive ToggleButtonGroup emits `null` when the reviewer clicks the button that is
     * already selected, so deselecting `needs` fell through and emitted an unassessed image
     * still carrying its categories and comment. That is the same contradictory pair the
     * `correct` branch exists to prevent, and the API refuses it with a 400.
     */
    const setOutcome = (outcome: QcOutcome | null) => {
        const keepsFeedback = outcome === 'needs';
        onChange({
            outcome,
            categoryIds: keepsFeedback ? value.categoryIds : [],
            comment: keepsFeedback ? value.comment : '',
        });
    };

    return (
        <Box sx={{ mt: 1.5 }} data-testid="image-assessment" data-read-only={readOnly ? 'true' : undefined}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                <Translate id="hospital.quality-check.review.assessment" />
            </Typography>

            {readOnly ? (
                <React.Fragment>
                    {/*
                      * LOCKED is read-only for everyone including the reviewer who
                      * approved, so this is not a disabled form — there is nothing to
                      * disable. Disabled inputs would suggest the visit could be reopened
                      * from here, which it cannot.
                      */}
                    <Typography variant="body2" data-testid="outcome-static" data-outcome={value.outcome ?? 'unassessed'}>
                        <Translate
                            id={value.outcome === 'correct'
                                ? 'hospital.quality-check.review.correct'
                                : value.outcome === 'needs'
                                    ? 'hospital.quality-check.review.needs-feedback'
                                    : 'hospital.quality-check.review.unassessed'}
                        />
                    </Typography>
                    <CategoryFeedback categories={categories} value={value} onChange={() => undefined} readOnly />
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={value.outcome}
                        onChange={(_event, next) => setOutcome(next as QcOutcome | null)}
                        data-testid="outcome-toggle"
                        sx={{ mt: 0.5 }}
                    >
                        <ToggleButton value="correct" data-testid="outcome-correct">
                            <Translate id="hospital.quality-check.review.correct" />
                        </ToggleButton>
                        <ToggleButton value="needs" data-testid="outcome-needs">
                            <Translate id="hospital.quality-check.review.needs-feedback" />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {value.outcome === 'needs' && (
                        <Box sx={{ mt: 1 }}>
                            <CategoryFeedback
                                categories={categories}
                                value={value}
                                onChange={(next) => onChange({ ...value, ...next })}
                                showSectionHeadings
                                // Names the reader: this note goes to the dermatologist who
                                // took the photograph, not to another reviewer.
                                notePlaceholderId="hospital.quality-check.review.note-placeholder"
                            />
                        </Box>
                    )}
                </React.Fragment>
            )}
        </Box>
    );
}
